"use client";

import { useState, useCallback } from "react";
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  HelpCircle,
} from "lucide-react";
import type {
  ResourceField,
  GroupField,
  DictField,
  TypedDictField,
  TableSelectField,
  DataGridField,
  DropdownOption,
  ShowWhenCondition,
} from "./types";
import { TableSelectFieldRenderer } from "./TableSelectFieldRenderer";
import { DataGridFieldRenderer } from "./DataGridFieldRenderer";

// =============================================================================
// FIELD RENDERER - Recursive component for deep nesting
// =============================================================================

interface FieldRendererProps {
  field: ResourceField;
  value: unknown;
  onChange: (name: string, value: unknown) => void;
  path: string; // Dot-separated path for nested fields
  depth: number;
  errors?: Record<string, string>;
  mode?: "create" | "edit" | "view";
  allValues?: Record<string, unknown>; // All form values for conditional visibility
}

// =============================================================================
// CONDITIONAL VISIBILITY HELPER
// =============================================================================

function evaluateShowWhen(
  condition: ShowWhenCondition | ShowWhenCondition[],
  allValues: Record<string, unknown>
): boolean {
  // Handle array of conditions (AND logic)
  if (Array.isArray(condition)) {
    return condition.every((c) => evaluateSingleCondition(c, allValues));
  }
  return evaluateSingleCondition(condition, allValues);
}

function evaluateSingleCondition(
  condition: ShowWhenCondition,
  allValues: Record<string, unknown>
): boolean {
  const fieldValue = getValueByPath(allValues, condition.field);

  // Check equals
  if (condition.equals !== undefined) {
    return fieldValue === condition.equals;
  }

  // Check not_equals
  if (condition.not_equals !== undefined) {
    return fieldValue !== condition.not_equals;
  }

  // Check in array
  if (condition.in !== undefined) {
    return condition.in.includes(fieldValue);
  }

  // Check not in array
  if (condition.not_in !== undefined) {
    return !condition.not_in.includes(fieldValue);
  }

  // Check is_empty
  if (condition.is_empty !== undefined) {
    const isEmpty = fieldValue === null ||
                    fieldValue === undefined ||
                    fieldValue === "" ||
                    (Array.isArray(fieldValue) && fieldValue.length === 0);
    return condition.is_empty ? isEmpty : !isEmpty;
  }

  // Check is_not_empty
  if (condition.is_not_empty !== undefined) {
    const isEmpty = fieldValue === null ||
                    fieldValue === undefined ||
                    fieldValue === "" ||
                    (Array.isArray(fieldValue) && fieldValue.length === 0);
    return condition.is_not_empty ? !isEmpty : isEmpty;
  }

  return true;
}

function getValueByPath(obj: Record<string, unknown>, path: string): unknown {
  return path.split(".").reduce((acc: unknown, key) => {
    if (acc && typeof acc === "object") {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

export function FieldRenderer({
  field,
  value,
  onChange,
  path,
  depth,
  errors = {},
  mode = "create",
  allValues = {},
}: FieldRendererProps) {
  const fieldPath = path ? `${path}.${field.name}` : field.name;
  const error = errors[fieldPath];
  const isViewMode = mode === "view";
  const isHidden = field.show === false;

  // Check show_when condition
  if (field.show_when && !evaluateShowWhen(field.show_when, allValues)) {
    return null;
  }

  if (isHidden) return null;

  // Route to specific renderer based on type
  switch (field.type) {
    case "info":
      // Info field - display-only, no input
      return (
        <div className="rounded border border-blue-800/50 bg-blue-900/20 p-3 mb-2">
          <div className="flex items-start gap-2">
            <HelpCircle className="h-4 w-4 text-blue-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-xs font-medium text-blue-300">{field.display_name}</p>
              {field.description && (
                <p className="text-xs text-blue-200/70 mt-1">{field.description}</p>
              )}
            </div>
          </div>
        </div>
      );

    case "string":
      return (
        <StringFieldRenderer
          field={field}
          value={value as string}
          onChange={(v) => onChange(fieldPath, v)}
          error={error}
          disabled={isViewMode || field.editable === false}
        />
      );

    case "secret":
      return (
        <SecretFieldRenderer
          field={field}
          value={value as string}
          onChange={(v) => onChange(fieldPath, v)}
          error={error}
          disabled={isViewMode || field.editable === false}
        />
      );

    case "integer":
    case "float":
      return (
        <NumberFieldRenderer
          field={field}
          value={value as number}
          onChange={(v) => onChange(fieldPath, v)}
          error={error}
          disabled={isViewMode || field.editable === false}
        />
      );

    case "boolean":
      return (
        <BooleanFieldRenderer
          field={field}
          value={value as boolean}
          onChange={(v) => onChange(fieldPath, v)}
          disabled={isViewMode || field.editable === false}
        />
      );

    case "code":
      return (
        <CodeFieldRenderer
          field={field}
          value={value}
          onChange={(v) => onChange(fieldPath, v)}
          error={error}
          disabled={isViewMode || field.editable === false}
        />
      );

    case "dropdown":
      return (
        <DropdownFieldRenderer
          field={field}
          value={value as string}
          onChange={(v) => onChange(fieldPath, v)}
          error={error}
          disabled={isViewMode || field.editable === false}
        />
      );

    case "multi_select":
      return (
        <MultiSelectFieldRenderer
          field={field}
          value={value as string[]}
          onChange={(v) => onChange(fieldPath, v)}
          error={error}
          disabled={isViewMode || field.editable === false}
        />
      );

    case "group":
      return (
        <GroupFieldRenderer
          field={field as GroupField}
          value={value}
          onChange={onChange}
          path={fieldPath}
          depth={depth + 1}
          errors={errors}
          mode={mode}
          allValues={allValues}
        />
      );

    case "dict":
      return (
        <DictFieldRenderer
          field={field as DictField}
          value={value}
          onChange={onChange}
          path={fieldPath}
          depth={depth + 1}
          errors={errors}
          mode={mode}
        />
      );

    case "typed_dict":
      return (
        <TypedDictFieldRenderer
          field={field as unknown as TypedDictField}
          value={value}
          onChange={onChange}
          path={fieldPath}
          depth={depth + 1}
          errors={errors}
          mode={mode}
        />
      );

    case "table_select":
      return (
        <TableSelectFieldRenderer
          field={field as TableSelectField}
          value={value as string | string[] | undefined}
          onChange={(v) => onChange(fieldPath, v)}
          error={error}
          disabled={isViewMode || field.editable === false}
        />
      );

    case "data_grid":
      return (
        <DataGridFieldRenderer
          field={field as DataGridField}
          value={value as Record<string, unknown>[] | undefined}
          onChange={(v) => onChange(fieldPath, v)}
          error={error}
          disabled={isViewMode || field.editable === false}
        />
      );

    default:
      return (
        <div className="text-red-400 text-sm">
          Unknown field type: {(field as ResourceField).type}
        </div>
      );
  }
}

// =============================================================================
// FIELD WRAPPER - Common label, description, error display
// =============================================================================

interface FieldWrapperProps {
  field: ResourceField;
  error?: string;
  children: React.ReactNode;
}

function FieldWrapper({ field, error, children }: FieldWrapperProps) {
  return (
    <div className="space-y-0.5">
      <label className="flex items-center gap-1 text-xs text-foreground">
        {field.display_name}
        {field.required && <span className="text-red-400">*</span>}
        {field.description && (
          <span className="group relative">
            <HelpCircle className="h-3 w-3 text-muted-foreground cursor-help" />
            <span className="absolute left-0 top-4 z-10 hidden w-48 rounded bg-muted p-1.5 text-[10px] text-foreground shadow-lg group-hover:block">
              {field.description}
            </span>
          </span>
        )}
      </label>
      {children}
      {error && <p className="text-[10px] text-red-400">{error}</p>}
    </div>
  );
}

// =============================================================================
// STRING FIELD
// =============================================================================

interface StringFieldRendererProps {
  field: ResourceField;
  value: string | undefined;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
}

function StringFieldRenderer({
  field,
  value,
  onChange,
  error,
  disabled,
}: StringFieldRendererProps) {
  const isMultiline = "multiline" in field && field.multiline;

  return (
    <FieldWrapper field={field} error={error}>
      {isMultiline ? (
        <textarea
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          disabled={disabled}
          rows={2}
          className="w-full rounded border border-border bg-muted px-2 py-1 text-xs text-foreground placeholder-muted-foreground focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:opacity-50"
        />
      ) : (
        <input
          type="text"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          disabled={disabled}
          className="w-full rounded border border-border bg-muted px-2 py-1 text-xs text-foreground placeholder-muted-foreground focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:opacity-50"
        />
      )}
    </FieldWrapper>
  );
}

// =============================================================================
// SECRET FIELD
// =============================================================================

function SecretFieldRenderer({
  field,
  value,
  onChange,
  error,
  disabled,
}: StringFieldRendererProps) {
  const [showSecret, setShowSecret] = useState(false);

  return (
    <FieldWrapper field={field} error={error}>
      <div className="relative">
        <input
          type={showSecret ? "text" : "password"}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          disabled={disabled}
          className="w-full rounded border border-border bg-muted px-2 py-1 pr-8 text-xs text-foreground placeholder-muted-foreground focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:opacity-50"
        />
        <button
          type="button"
          onClick={() => setShowSecret(!showSecret)}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          {showSecret ? (
            <EyeOff className="h-3 w-3" />
          ) : (
            <Eye className="h-3 w-3" />
          )}
        </button>
      </div>
      <p className="text-[10px] text-amber-500/70">
        Sensitive field - stored securely
      </p>
    </FieldWrapper>
  );
}

// =============================================================================
// NUMBER FIELD
// =============================================================================

interface NumberFieldRendererProps {
  field: ResourceField;
  value: number | undefined;
  onChange: (value: number) => void;
  error?: string;
  disabled?: boolean;
}

function NumberFieldRenderer({
  field,
  value,
  onChange,
  error,
  disabled,
}: NumberFieldRendererProps) {
  const rangeSpec = "range_spec" in field ? field.range_spec : undefined;
  const isFloat = field.type === "float";

  return (
    <FieldWrapper field={field} error={error}>
      <div className="flex items-center gap-3">
        <input
          type="number"
          value={value ?? ""}
          onChange={(e) => {
            const v = isFloat
              ? parseFloat(e.target.value)
              : parseInt(e.target.value);
            if (!isNaN(v)) onChange(v);
          }}
          min={rangeSpec?.min}
          max={rangeSpec?.max}
          step={rangeSpec?.step ?? (isFloat ? 0.1 : 1)}
          disabled={disabled}
          className="w-32 rounded border border-border bg-muted px-2 py-1 text-xs text-foreground focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:opacity-50"
        />
        {rangeSpec && (
          <>
            <input
              type="range"
              value={value ?? rangeSpec.min}
              onChange={(e) => {
                const v = isFloat
                  ? parseFloat(e.target.value)
                  : parseInt(e.target.value);
                onChange(v);
              }}
              min={rangeSpec.min}
              max={rangeSpec.max}
              step={rangeSpec.step}
              disabled={disabled}
              className="flex-1"
            />
            <span className="text-xs text-muted-foreground">
              {rangeSpec.min} - {rangeSpec.max}
            </span>
          </>
        )}
      </div>
    </FieldWrapper>
  );
}

// =============================================================================
// BOOLEAN FIELD
// =============================================================================

interface BooleanFieldRendererProps {
  field: ResourceField;
  value: boolean | undefined;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}

function BooleanFieldRenderer({
  field,
  value,
  onChange,
  disabled,
}: BooleanFieldRendererProps) {
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        role="switch"
        aria-checked={value ?? false}
        onClick={() => onChange(!value)}
        disabled={disabled}
        className={`relative h-6 w-11 rounded-full transition-colors ${
          value ? "bg-emerald-600" : "bg-secondary"
        } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <span
          className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
            value ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </button>
      <label className="text-sm text-foreground">
        {field.display_name}
        {field.description && (
          <span className="ml-2 text-xs text-muted-foreground">
            ({field.description})
          </span>
        )}
      </label>
    </div>
  );
}

// =============================================================================
// CODE FIELD
// =============================================================================

interface CodeFieldRendererProps {
  field: ResourceField;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  disabled?: boolean;
}

function CodeFieldRenderer({
  field,
  value,
  onChange,
  error,
  disabled,
}: CodeFieldRendererProps) {
  const language = "language" in field ? field.language : "json";
  const stringValue =
    typeof value === "string" ? value : JSON.stringify(value, null, 2);

  return (
    <FieldWrapper field={field} error={error}>
      <div className="relative">
        <div className="absolute right-2 top-2 text-xs text-muted-foreground">
          {language}
        </div>
        <textarea
          value={stringValue ?? ""}
          onChange={(e) => {
            const raw = e.target.value;
            if (language === "json") {
              try {
                onChange(JSON.parse(raw));
              } catch {
                onChange(raw); // Keep as string if invalid JSON
              }
            } else {
              onChange(raw);
            }
          }}
          disabled={disabled}
          rows={8}
          className="w-full rounded border border-border bg-card px-3 py-2 font-mono text-sm text-foreground focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:opacity-50"
        />
      </div>
    </FieldWrapper>
  );
}

// =============================================================================
// DROPDOWN FIELD
// =============================================================================

interface DropdownFieldRendererProps {
  field: ResourceField;
  value: string | undefined;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
}

function DropdownFieldRenderer({
  field,
  value,
  onChange,
  error,
  disabled,
}: DropdownFieldRendererProps) {
  const options: DropdownOption[] = "options" in field ? field.options : [];

  return (
    <FieldWrapper field={field} error={error}>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full rounded border border-border bg-muted px-2 py-1 text-xs text-foreground focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:opacity-50"
      >
        <option value="">Select...</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </FieldWrapper>
  );
}

// =============================================================================
// MULTI-SELECT FIELD
// =============================================================================

interface MultiSelectFieldRendererProps {
  field: ResourceField;
  value: string[] | undefined;
  onChange: (value: string[]) => void;
  error?: string;
  disabled?: boolean;
}

function MultiSelectFieldRenderer({
  field,
  value = [],
  onChange,
  error,
  disabled,
}: MultiSelectFieldRendererProps) {
  const options: DropdownOption[] = "options" in field ? field.options : [];

  const toggleOption = (optValue: string) => {
    if (value.includes(optValue)) {
      onChange(value.filter((v) => v !== optValue));
    } else {
      onChange([...value, optValue]);
    }
  };

  return (
    <FieldWrapper field={field} error={error}>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => toggleOption(opt.value)}
            disabled={disabled}
            className={`rounded-full px-3 py-1 text-xs transition-colors ${
              value.includes(opt.value)
                ? "bg-emerald-600 text-white"
                : "bg-secondary text-foreground hover:bg-secondary"
            } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </FieldWrapper>
  );
}

// =============================================================================
// GROUP FIELD - Recursive nesting with collapsible sections
// =============================================================================

interface GroupFieldRendererProps {
  field: GroupField;
  value: unknown;
  onChange: (name: string, value: unknown) => void;
  path: string;
  depth: number;
  errors?: Record<string, string>;
  mode?: "create" | "edit" | "view";
  allValues?: Record<string, unknown>;
}

function GroupFieldRenderer({
  field,
  value,
  onChange,
  path,
  depth,
  errors = {},
  mode,
  allValues = {},
}: GroupFieldRendererProps) {
  const [collapsed, setCollapsed] = useState(field.collapsed ?? false);
  const isViewMode = mode === "view";

  // Handle is_list (repeatable rows)
  if (field.is_list) {
    const rows = (value as Record<string, unknown>[]) || [{}];

    const addRow = () => {
      const newRows = [...rows, {}];
      onChange(path, newRows);
    };

    const removeRow = (index: number) => {
      const newRows = rows.filter((_, i) => i !== index);
      onChange(path, newRows);
    };

    const updateRow = (index: number, fieldPath: string, fieldValue: unknown) => {
      const newRows = [...rows];
      const relativePath = fieldPath.replace(`${path}[${index}].`, "");
      setNestedValue(newRows[index], relativePath, fieldValue);
      onChange(path, newRows);
    };

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-foreground">
            {field.display_name}
            {field.required && <span className="text-red-400 ml-1">*</span>}
          </label>
          {!isViewMode && (
            <button
              type="button"
              onClick={addRow}
              className="flex items-center gap-1 rounded bg-secondary px-2 py-1 text-xs text-foreground hover:bg-secondary"
            >
              <Plus className="h-3 w-3" />
              {field.list_add_label || "Add Row"}
            </button>
          )}
        </div>

        <div className="space-y-2">
          {rows.map((row, index) => (
            <div
              key={index}
              className="relative rounded border border-border bg-muted/50 p-3"
            >
              {!isViewMode && rows.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeRow(index)}
                  className="absolute right-2 top-2 text-muted-foreground hover:text-red-400"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
              <div
                className={`grid gap-3 ${
                  field.columns === 2
                    ? "grid-cols-2"
                    : field.columns === 3
                    ? "grid-cols-3"
                    : "grid-cols-1"
                }`}
              >
                {field.fields.map((nestedField) => (
                  <FieldRenderer
                    key={nestedField.name}
                    field={nestedField}
                    value={getNestedValue(row, nestedField.name)}
                    onChange={(fieldPath, fieldValue) =>
                      updateRow(index, fieldPath, fieldValue)
                    }
                    path={`${path}[${index}]`}
                    depth={depth}
                    errors={errors}
                    mode={mode}
                    allValues={allValues}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Single group (collapsible section)
  const groupValue = (value as Record<string, unknown>) || {};

  const updateGroupField = (fieldPath: string, fieldValue: unknown) => {
    const newValue = { ...groupValue };
    const relativePath = fieldPath.replace(`${path}.`, "");
    setNestedValue(newValue, relativePath, fieldValue);
    onChange(path, newValue);
  };

  return (
    <div
      className={`rounded border border-border ${
        depth > 0 ? "bg-muted/30" : "bg-muted/50"
      }`}
    >
      {/* Collapsible header */}
      {field.collapsible !== false && (
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          className="flex w-full items-center gap-2 p-3 text-left text-sm font-medium text-foreground hover:bg-secondary/30"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
          {field.display_name}
          {field.required && <span className="text-red-400">*</span>}
          {field.description && (
            <span className="ml-2 text-xs text-muted-foreground font-normal">
              {field.description}
            </span>
          )}
        </button>
      )}

      {/* Content */}
      {!collapsed && (
        <div
          className={`p-3 ${field.collapsible !== false ? "border-t border-border" : ""}`}
        >
          <div
            className={`grid gap-4 ${
              field.columns === 2
                ? "grid-cols-2"
                : field.columns === 3
                ? "grid-cols-3"
                : field.columns === 4
                ? "grid-cols-4"
                : "grid-cols-1"
            } ${field.field_direction === "row" ? "flex flex-row flex-wrap" : ""}`}
          >
            {field.fields.map((nestedField) => (
              <FieldRenderer
                key={nestedField.name}
                field={nestedField}
                value={getNestedValue(groupValue, nestedField.name)}
                onChange={updateGroupField}
                path={path}
                depth={depth}
                errors={errors}
                mode={mode}
                allValues={allValues}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// DICT FIELD - Key-value pairs
// =============================================================================

interface DictFieldRendererProps {
  field: DictField;
  value: unknown;
  onChange: (name: string, value: unknown) => void;
  path: string;
  depth: number;
  errors?: Record<string, string>;
  mode?: "create" | "edit" | "view";
}

function DictFieldRenderer({
  field,
  value,
  onChange,
  path,
  depth,
  errors = {},
  mode,
}: DictFieldRendererProps) {
  const isViewMode = mode === "view";

  if (field.is_list) {
    // List of key-value pairs
    const rows = (value as { key: string; value: unknown }[]) || [];

    const addRow = () => {
      onChange(path, [...rows, { key: "", value: "" }]);
    };

    const removeRow = (index: number) => {
      onChange(
        path,
        rows.filter((_, i) => i !== index)
      );
    };

    const updateRow = (
      index: number,
      field: "key" | "value",
      newValue: unknown
    ) => {
      const newRows = [...rows];
      newRows[index] = { ...newRows[index], [field]: newValue };
      onChange(path, newRows);
    };

    return (
      <FieldWrapper field={field}>
        <div className="space-y-2">
          {rows.map((row, index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                type="text"
                value={row.key}
                onChange={(e) => updateRow(index, "key", e.target.value)}
                placeholder={field.key_field.placeholder || "Key"}
                disabled={isViewMode}
                className="w-1/3 rounded border border-border bg-muted px-2 py-1.5 text-sm text-foreground"
              />
              <span className="text-muted-foreground">=</span>
              <input
                type="text"
                value={String(row.value ?? "")}
                onChange={(e) => updateRow(index, "value", e.target.value)}
                placeholder="Value"
                disabled={isViewMode}
                className="flex-1 rounded border border-border bg-muted px-2 py-1.5 text-sm text-foreground"
              />
              {!isViewMode && (
                <button
                  type="button"
                  onClick={() => removeRow(index)}
                  className="text-muted-foreground hover:text-red-400"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          {!isViewMode && (
            <button
              type="button"
              onClick={addRow}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <Plus className="h-3 w-3" />
              Add entry
            </button>
          )}
        </div>
      </FieldWrapper>
    );
  }

  // Single dict as object
  const dictValue = (value as Record<string, unknown>) || {};
  const entries = Object.entries(dictValue);

  return (
    <FieldWrapper field={field}>
      <div className="space-y-2">
        {entries.map(([key, val], index) => (
          <div key={index} className="flex items-center gap-2">
            <span className="w-1/3 text-sm text-muted-foreground">{key}:</span>
            <span className="flex-1 text-sm text-foreground">{String(val)}</span>
          </div>
        ))}
      </div>
    </FieldWrapper>
  );
}

// =============================================================================
// TYPED DICT FIELD - Key-value with type selector
// =============================================================================

interface TypedDictFieldRendererProps {
  field: TypedDictField;
  value: unknown;
  onChange: (name: string, value: unknown) => void;
  path: string;
  depth: number;
  errors?: Record<string, string>;
  mode?: "create" | "edit" | "view";
}

function TypedDictFieldRenderer({
  field,
  value,
  onChange,
  path,
  depth,
  errors = {},
  mode,
}: TypedDictFieldRendererProps) {
  const isViewMode = mode === "view";
  const rows = (value as { key: string; type: string; value: unknown }[]) || [];

  const addRow = () => {
    onChange(path, [...rows, { key: "", type: "", value: "" }]);
  };

  const removeRow = (index: number) => {
    onChange(
      path,
      rows.filter((_, i) => i !== index)
    );
  };

  const updateRow = (
    index: number,
    rowField: "key" | "type" | "value",
    newValue: unknown
  ) => {
    const newRows = [...rows];
    newRows[index] = { ...newRows[index], [rowField]: newValue };
    onChange(path, newRows);
  };

  const typeOptions = field.type_field.options || [];

  return (
    <FieldWrapper field={field}>
      <div className="space-y-2">
        {rows.map((row, index) => (
          <div key={index} className="flex items-center gap-2">
            <input
              type="text"
              value={row.key}
              onChange={(e) => updateRow(index, "key", e.target.value)}
              placeholder="Key"
              disabled={isViewMode}
              className="w-1/4 rounded border border-border bg-muted px-2 py-1.5 text-sm text-foreground"
            />
            <select
              value={row.type}
              onChange={(e) => updateRow(index, "type", e.target.value)}
              disabled={isViewMode}
              className="w-1/4 rounded border border-border bg-muted px-2 py-1.5 text-sm text-foreground"
            >
              <option value="">Type</option>
              {typeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={String(row.value ?? "")}
              onChange={(e) => updateRow(index, "value", e.target.value)}
              placeholder="Value"
              disabled={isViewMode}
              className="flex-1 rounded border border-border bg-muted px-2 py-1.5 text-sm text-foreground"
            />
            {!isViewMode && (
              <button
                type="button"
                onClick={() => removeRow(index)}
                className="text-muted-foreground hover:text-red-400"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
        {!isViewMode && (
          <button
            type="button"
            onClick={addRow}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <Plus className="h-3 w-3" />
            Add entry
          </button>
        )}
      </div>
    </FieldWrapper>
  );
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split(".").reduce((acc: unknown, key) => {
    if (acc && typeof acc === "object") {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

function setNestedValue(
  obj: Record<string, unknown>,
  path: string,
  value: unknown
): void {
  const keys = path.split(".");
  let current = obj;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!(key in current) || typeof current[key] !== "object") {
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }

  current[keys[keys.length - 1]] = value;
}
